// (c) Santosh Sahoo
import {
  defaults,
  get as getPath,
  isArray,
  isFunction,
  isNaN,
  isNil,
  isPlainObject,
  isString,
} from "lodash";
import { formatDate } from "./common";

const resources = {
  fieldIsRequired: "This field is required!",
  invalidFormat: "Invalid!",

  numberTooSmall: "The number is too small! Minimum: {0}",
  numberTooBig: "The number is too big! Maximum: {0}",
  invalidNumber: "Invalid number",
  invalidInteger: "The value is not an integer",

  textTooSmall: "The length of text is too small!",
  textTooBig: "The length of text is too big!",
  thisNotText: "This is not a text!",

  thisNotArray: "This is not an array!",

  selectMinItems: "Select minimum {0} items!",
  selectMaxItems: "Select maximum {0} items!",

  invalidDate: "Invalid date!",
  dateIsEarly: "The date is too early!",
  dateIsLate: "The date is too late!",

  invalidEmail: "Invalid e-mail address!",
  invalidURL: "Invalid URL!",

  invalidCard: "Invalid!",
  invalidCardNumber: "Invalid card number!",

  invalidTextContainNumber:
    "Invalid text! Cannot contain numbers or special characters",
  invalidTextContainSpec: "Invalid text! Cannot contain special characters",
};

function checkEmpty(value, required, messages = resources) {
  if (!value || (isArray(value) && value.length < 1)) {
    if (required) {
      return [formatMsg(messages.fieldIsRequired)];
    } else {
      return [];
    }
  }
  return null;
}

function formatMsg(text: string, ...arr: string[]) {
  if (!text || !arr.length) return text;
  return text.replace(/\{(\d+)\}/g, (match, index) => {
    return index < arr.length ? arr[index] : match;
  });
}

function regexp(
  value,
  { pattern, required = false },
  model,
  messages = resources,
) {
  const res = checkEmpty(value, required, messages);
  if (res != null) return res;

  if (!isNil(pattern)) {
    const re = pattern instanceof RegExp ? pattern : new RegExp(pattern);
    if (!re.test(value)) {
      return [formatMsg(messages.invalidFormat)];
    }
  }
}

const validators = {
  resources,

  required(value, opts, model, messages = resources) {
    return checkEmpty(value, opts, messages);
  },

  minLength(value, opts, model, messages = resources) {
    const err: string[] = [];
    if (value?.length < opts) {
      err.push(formatMsg(messages.textTooSmall, value?.length, opts));
    }
    return err;
  },

  number(value, opts, model, messages = resources) {
    if (!value) return [];
    if (!/^-?\d*(\.\d+)?$/.test(value)) {
      return [formatMsg(messages.invalidNumber)];
    }
    value = parseFloat(value);
    const err: string[] = [];

    if (!isNil(opts.min) && value < opts.min) {
      err.push(formatMsg(messages.numberTooSmall, opts.min));
    }

    if (!isNil(opts.max) && value > opts.max) {
      err.push(formatMsg(messages.numberTooBig, opts.max));
    }

    return err;
  },

  string(value, opts, model, messages = resources) {
    const res = checkEmpty(value, opts.required, messages);
    if (res != null) return res;

    const err: string[] = [];
    if (isString(value)) {
      if (!isNil(opts.min) && value.length < opts.min) {
        err.push(
          formatMsg(messages.textTooSmall, String(value.length), opts.min),
        );
      }

      if (!isNil(opts.max) && value.length > opts.max) {
        err.push(
          formatMsg(messages.textTooBig, String(value.length), opts.max),
        );
      }
    } else {
      err.push(formatMsg(messages.thisNotText));
    }

    return err;
  },

  array(value, opts, model, messages = resources) {
    if (opts.required) {
      if (!isArray(value)) {
        return [formatMsg(messages.thisNotArray)];
      }

      if (value.length === 0) {
        return [formatMsg(messages.fieldIsRequired)];
      }
    }

    if (!isNil(value)) {
      if (!isNil(opts.min) && value.length < opts.min) {
        return [formatMsg(messages.selectMinItems, opts.min)];
      }

      if (!isNil(opts.max) && value.length > opts.max) {
        return [formatMsg(messages.selectMaxItems, opts.max)];
      }
    }
  },

  date(value, opts, model, messages = resources) {
    const res = checkEmpty(value, opts.required, messages);
    if (res != null) return res;

    const m = new Date(value);
    if (isNaN(m.getDate())) {
      return [formatMsg(messages.invalidDate)];
    }

    const err: string[] = [];

    if (!isNil(opts.min)) {
      const min = new Date(opts.min);
      if (m.valueOf() < min.valueOf()) {
        err.push(
          formatMsg(messages.dateIsEarly, formatDate(m)!, formatDate(min)!),
        );
      }
    }

    if (!isNil(opts.max)) {
      const max = new Date(opts.max);
      if (m.valueOf() > max.valueOf()) {
        err.push(
          formatMsg(messages.dateIsLate, formatDate(m)!, formatDate(max)!),
        );
      }
    }

    return err;
  },

  regexp,

  email(value, opts, model, messages = resources) {
    const res = checkEmpty(value, opts.required, messages);
    if (res != null) return res;

    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; // eslint-disable-line no-useless-escape
    if (!re.test(value)) {
      return [formatMsg(messages.invalidEmail)];
    }
  },

  url(value, opts, model, messages = resources) {
    const res = checkEmpty(value, opts.required, messages);
    if (res != null) return res;

    const re =
      /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,4}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g; // eslint-disable-line no-useless-escape
    if (!re.test(value)) {
      return [formatMsg(messages.invalidURL)];
    }
  },

  creditCard(value, opts, model, messages = resources) {
    const res = checkEmpty(value, opts.required, messages);
    if (res != null) return res;

    /*  From validator.js code
			https://github.com/chriso/validator.js/blob/master/src/lib/isCreditCard.js
		*/
    const creditCard =
      /^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|6(?:011|5[0-9][0-9])[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\d{3})\d{11})$/;
    const sanitized = value.replace(/[^0-9]+/g, "");
    if (!creditCard.test(sanitized)) {
      return [formatMsg(messages.invalidCard)];
    }
    let sum = 0;
    let digit;
    let tmpNum;
    let shouldDouble;
    for (let i = sanitized.length - 1; i >= 0; i--) {
      digit = sanitized.substring(i, i + 1);
      tmpNum = parseInt(digit, 10);
      if (shouldDouble) {
        tmpNum *= 2;
        if (tmpNum >= 10) {
          sum += (tmpNum % 10) + 1;
        } else {
          sum += tmpNum;
        }
      } else {
        sum += tmpNum;
      }
      shouldDouble = !shouldDouble;
    }

    if (!(sum % 10 === 0 ? sanitized : false)) {
      return [formatMsg(messages.invalidCardNumber)];
    }
  },

  alpha(value, opts, model, messages = resources) {
    const res = checkEmpty(value, opts.required, messages);
    if (res != null) return res;

    const re = /^[a-zA-Z]*$/;
    if (!re.test(value)) {
      return [formatMsg(messages.invalidTextContainNumber)];
    }
  },

  alphaNumeric(value, opts, model, messages = resources) {
    const res = checkEmpty(value, opts.required, messages);
    if (res != null) return res;

    const re = /^[a-zA-Z0-9]*$/;
    if (!re.test(value)) {
      return [formatMsg(messages.invalidTextContainSpec)];
    }
  },
  phone(value, _opts, model, messages = resources) {
    return regexp(
      value,
      {
        pattern: /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
      },
      model,
      messages,
    );
  },
};

Object.keys(validators).forEach((name) => {
  const fn = validators[name];
  if (isFunction(fn)) {
    fn["locale"] = (customMessages) => (value, opts, model) =>
      fn(value, opts, model, defaults(customMessages, resources));
  }
});

type Rule =
  | boolean
  | string
  | RegExp
  | Array<unknown>
  | Record<string, unknown>
  | ((
      val: unknown,
      opts: unknown,
      obj: any,
    ) => string[] | boolean | undefined | void);

export function validate(rules: Record<string, Rule>, obj: unknown) {
  const errorMessages: any = {};
  let hasError = false;

  function doValidate(rule: Rule, key: string, opts = {}) {
    if (rule === true) rule = "required";
    if (rule instanceof RegExp) {
      opts = { pattern: rule };
      rule = "regexp";
    }
    const fn = isFunction(rule) ? rule : validators[rule as string];
    if (fn) {
      const val = getPath(obj, key);
      const errors = fn(val, opts, obj);
      if (errors?.length) {
        errorMessages[key] = errors[0];
        hasError = true;
      }
    }
  }

  for (const key in rules) {
    const rule = rules[key];
    if (Array.isArray(rule)) {
      for (const validatorName of rule) {
        doValidate(validatorName as Rule[], key);
      }
    } else if (isPlainObject(rule)) {
      for (const validatorName in rule as Record<string, Rule>) {
        doValidate(validatorName, key, rule[validatorName]);
      }
    } else {
      doValidate(rule, key);
    }
  }

  return {
    hasError,
    errorMessages,
  };
}

export default validate;
